import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

// Connects to data-controller="survey-question-tree"
//
// Renders the survey step's question subset as a section-grouped checkbox tree.
// Section toggles select/deselect all of their questions and reflect an
// all/none/indeterminate tri-state. A controlling question (one referenced by a
// selected question's display condition) cannot be unchecked while a dependent
// stays selected; an inline message names the dependents.
//
// DOM contract:
//   question checkbox  -> target "question", data-question-id, data-section-id,
//                         data-controller-ids (JSON array of question ids it depends on)
//   section checkbox   -> target "sectionToggle", data-section-id
//   selection count    -> target "count"
//   reset notice       -> target "notice"  (revealed on survey change)
//   lock message       -> target "lockMessage"
export default class extends Controller {
  static targets = ["question", "sectionToggle", "count", "notice", "lockMessage"];

  connect() {
    this.sectionToggleTargets.forEach((toggle) => this.refreshSection(toggle.dataset.sectionId));
    this.updateCount();
  }

  // Toggle every question in a section to match the section checkbox.
  toggleSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId;
    const checked = event.currentTarget.checked;
    const questions = this.questionsForSection(sectionId);

    if (!checked) {
      // Same lock toggleQuestion enforces: unchecking the section must not
      // strand selected dependents in other sections. (In-section dependents
      // are unchecked together with their controllers, so they can't strand.)
      const crossSectionDependents = questions.flatMap((question) =>
        this.selectedDependentsOf(question).filter((dep) => dep.dataset.sectionId !== sectionId),
      );

      if (crossSectionDependents.length > 0) {
        this.refreshSection(sectionId); // snap the toggle back to its tri-state
        this.showLockMessage([...new Set(crossSectionDependents)]);
        return;
      }
    }

    questions.forEach((question) => {
      question.checked = checked;
    });

    this.clearLockMessage();
    this.refreshSection(sectionId);
    this.updateCount();
  }

  // A single question changed. Enforce the dependency lock, then refresh state.
  toggleQuestion(event) {
    const question = event.currentTarget;

    if (!question.checked) {
      const dependents = this.selectedDependentsOf(question);
      if (dependents.length > 0) {
        question.checked = true;
        this.showLockMessage(dependents);
        return;
      }
    }

    this.clearLockMessage();
    this.refreshSection(question.dataset.sectionId);
    this.updateCount();
  }

  // Survey changed in the picker: warn that the subset resets to all-selected.
  surveyChanged() {
    if (this.hasNoticeTarget) {
      this.noticeTarget.textContent = i18n.t("workflow_steps.survey_question_tree.reset_notice");
      this.noticeTarget.classList.remove("hidden");
    }
  }

  // --- private ---

  questionsForSection(sectionId) {
    return this.questionTargets.filter((q) => q.dataset.sectionId === sectionId);
  }

  // Set a section checkbox to all / none / indeterminate from its questions.
  refreshSection(sectionId) {
    const toggle = this.sectionToggleTargets.find((t) => t.dataset.sectionId === sectionId);
    if (!toggle) return;

    const questions = this.questionsForSection(sectionId);
    const checkedCount = questions.filter((q) => q.checked).length;

    toggle.checked = checkedCount > 0 && checkedCount === questions.length;
    toggle.indeterminate = checkedCount > 0 && checkedCount < questions.length;
  }

  // Selected questions whose display condition references `question`.
  selectedDependentsOf(question) {
    const id = parseInt(question.dataset.questionId, 10);

    return this.questionTargets.filter((other) => {
      if (other === question || !other.checked) return false;
      return this.controllerIdsFor(other).includes(id);
    });
  }

  controllerIdsFor(question) {
    try {
      return JSON.parse(question.dataset.controllerIds || "[]").map((v) => parseInt(v, 10));
    } catch (_e) {
      return [];
    }
  }

  showLockMessage(dependents) {
    if (!this.hasLockMessageTarget) return;

    const names = dependents.map((d) => d.dataset.questionLabel).filter(Boolean).join(", ");
    this.lockMessageTarget.textContent = i18n.t("workflow_steps.survey_question_tree.controller_locked", {
      dependents: names,
    });
    this.lockMessageTarget.classList.remove("hidden");
  }

  clearLockMessage() {
    if (!this.hasLockMessageTarget) return;
    this.lockMessageTarget.textContent = "";
    this.lockMessageTarget.classList.add("hidden");
  }

  updateCount() {
    if (!this.hasCountTarget) return;

    const selected = this.questionTargets.filter((q) => q.checked).length;
    this.countTarget.textContent = i18n.t("workflow_steps.survey_question_tree.selected_count", {
      count: selected,
      total: this.questionTargets.length,
    });
  }
}
